import classNames from 'classnames';
import React, { useMemo } from 'react';

const styles = require('./CircularProgress.module.scss');

type ProgressContext = 'talent' | 'company' | 'neutral';

const STROKE_WIDTH = 8;

export interface CircularProgressProps {
  className?: string;
  context?: ProgressContext;
  label?: string;
  percentage: number;
  size?: number;
}

const CircularProgress: React.FC<CircularProgressProps> = ({
  className,
  context = 'neutral',
  label = 'Progress',
  percentage,
  size = 104,
}) => {
  const clampedPercentage = Math.min(100, Math.max(0, percentage));

  const { radius, circumference, offset } = useMemo(() => {
    const r = (size - STROKE_WIDTH) / 2;
    const c = 2 * Math.PI * r;
    return {
      radius: r,
      circumference: c,
      offset: c - (clampedPercentage / 100) * c,
    };
  }, [size, clampedPercentage]);

  const center = size / 2;

  return (
    <div
      className={classNames(styles.container, className)}
      style={{ width: size, height: size }}
      role="progressbar"
      aria-label={label}
      aria-valuenow={Math.round(clampedPercentage)}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <svg
        className={styles.svg}
        viewBox={`0 0 ${size} ${size}`}
        width={size}
        height={size}
        aria-hidden="true"
      >
        <circle
          className={styles.trail}
          cx={center}
          cy={center}
          r={radius}
          strokeWidth={STROKE_WIDTH}
        />
        {clampedPercentage > 0 && (
          <circle
            className={classNames(styles.path, styles[context])}
            cx={center}
            cy={center}
            r={radius}
            strokeWidth={STROKE_WIDTH}
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
          />
        )}
      </svg>
      <span className={styles.label} aria-hidden="true">{Math.round(clampedPercentage)}%</span>
    </div>
  );
};

export default CircularProgress;
